import React, { useContext, useEffect, useState, useRef } from 'react';
import { Checkbox, Dropdown, message, Popover, Tag, Tree } from 'antd';
import { FieldDataNode } from 'rc-tree/lib/interface';
import styles from './SpaceTree.module.less';
import { LayoutContext } from '../TuzhiVis';
import { Space } from '../../../interfaces';
import { selectSpaces, setActiveSpaceId, setCheckSpaceId } from '../tuzhiVisSlice';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import * as FlexLayout from 'flexlayout-react';
import { saveAs } from 'file-saver';
import TuzhiVisService from '../TuzhiVis.service';
import { InfoCircleOutlined } from '@ant-design/icons';
import { getTuzhiTheme } from '../../../config';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface SpaceTreeProps {
}

type SpaceTreeDataNode = FieldDataNode<{
  key: string;
  title: string | React.ReactNode;
  spaceId: number;
}>;
const semanticTypeMapping = {
  UNKNOWN: '未知类型',
  DES_SPECIFICATION: '设计说明',
  BUILDING_FLOOR_PLAN: '建筑平面图',
  FIRE_ALARM_PLAN: '自动报警平面图',
  EXTINGUISHER_PLAN: '灭火器平面布置图',
  ILLUMINATION_PLAN: '应急照明和疏散平面图',
  FIRE_WATER_SUPPLY_PLAN: '消防给水平面图',
  SMOKE_CONTROL_PLAN: '防排烟平面图',
  FIRE_COMPARTMENT_PLAN: '防火分区示意图'
};
const extractFloorNumber = (title: string): number => {
    // 匹配负数和正数
    const match = title.match(/-?\d+/);
    if (match) {
        return parseInt(match[0], 10);
    }
    // 处理文字楼层
    if (title.includes('屋顶') || title.includes('屋面')) return 100;
    if (title.includes('机房')) return 101;
    return 999;
};
function SpaceTree(props: SpaceTreeProps) {
  const spaces = useAppSelector(selectSpaces);
  const dispatch = useAppDispatch();

  const layoutContext = useContext(LayoutContext);

  const [spaceTree, setSpaceTree] = useState<SpaceTreeDataNode[]>([]);
  const [expandKeys, setExpandKeys] = useState<React.Key[]>([]);

  const [showUnknown, setShowUnknown] = useState(true);
  const [stats, setStats] = useState<{[key: string]: number}>({});
  const prevSpacesRef = useRef<Space[]>([]);
  useEffect(() => {
    // 通过线性目录构造目录树
    const treeRoot: SpaceTreeDataNode = {
      title: '项目根目录',
      key: 'root',
      spaceId: -1,
      children: [],
    };
    const stats: { [key: string]: number } = {};
    const newExpandKeys: string[] = ['root'];
    spaces.forEach((space: Space) => {
      const type = space.semanticType;
      stats[type] = (stats[type] || 0) + 1;
      if (space.name.includes('未知') && !showUnknown)
        return;
      const path = [...space.treePath, `${space.name}`]; // todo name是否和tree合并
      let currentNode = treeRoot;
      let currentKey = 'root';
      for (let i = 0; i < path.length; i++) {
        currentKey += '/' + path[i];
        if (!currentNode.children) {
          currentNode.children = [];
        }
        let node = currentNode.children.find(n => n.key === currentKey);
        // 如果当前空间被标记为删除则展示删除线
        let title: string | React.ReactNode = path[i];
        // TODO: 后续增加统一的属性操作工具
        if (space.properties.filter(p => p.key === '图名解析.删除' && p.value === '是').length > 0) {
          title = <span style={{ textDecoration: 'line-through' }}>{title}</span>;
        }
        if (!node) {
          node = {
            title,
            key: currentKey,
            spaceId: space.spaceId,
            children: [],
          };
          if (getTuzhiTheme() !== 'szzj' || node.title !== '单体多层融合空间') {
            currentNode.children.push(node);
            // 对当前层级的 children 按楼层数字排序
            currentNode.children.sort((a, b) => {
            const aTitle = String(a.title);
            const bTitle = String(b.title);
            // 让"单体多层融合空间"排在最前面
            if (aTitle === '单体多层融合空间') return -1;
            if (bTitle === '单体多层融合空间') return 1;
            // 其他按楼层数字排序
            const aNum = extractFloorNumber(aTitle);
            const bNum = extractFloorNumber(bTitle);
            return aNum - bNum;
          });
          newExpandKeys.push(currentKey);
          }
        }
        // @ts-ignore todo
        currentNode = node;
      }
    });

    setSpaceTree([treeRoot]);
    setStats(stats);
    const isFirstLoad = prevSpacesRef.current.length === 0;
    const isSpacesChanged = JSON.stringify(prevSpacesRef.current.map((s: Space) => s.spaceId)) !==
        JSON.stringify(spaces.map((s: Space) => s.spaceId));

    if (isFirstLoad) {
        setExpandKeys(newExpandKeys);
    } else if (isSpacesChanged) {
        setExpandKeys(prev => {
            const merged = [...prev];
            newExpandKeys.forEach(key => {
                if (!merged.includes(key)) {
                    merged.push(key);
                }
            });
            return merged;
        });
    }

    prevSpacesRef.current = spaces;
  }, [spaces, showUnknown]);

  const onExpand = (expandedKeysValue: React.Key[]) => {
    setExpandKeys(expandedKeysValue);
  };


  const onPreview = (spaceId: number) => {
    // 新建tab页或跳转tab页，打开当前资源
    const tabId = `space-${spaceId}`;
    dispatch(setActiveSpaceId(spaceId));
    if (layoutContext.model.getNodeById('element-properties')) {
      layoutContext.model.doAction(
        FlexLayout.Actions.selectTab('element-properties')
      );
    }
    if (layoutContext.model.getNodeById(tabId)) {
      layoutContext.model.doAction(
        FlexLayout.Actions.selectTab(tabId),
      );
    } else {
      layoutContext.model.doAction(FlexLayout.Actions.addNode(
        {
          id: tabId, type: 'tab',
          component: 'union-viewer',
          name: `[空间${spaceId}]${spaces.find(s => s.spaceId === spaceId)?.name}`,
          config: {spaceId: spaceId},
        },
        'viewer-pane', FlexLayout.DockLocation.CENTER, -1, true,
      ));
    }
  };

  const onCheck = (spaceId: number) => {
    dispatch(setCheckSpaceId(spaceId));
  };
  const generateStatsContent = (stats: { [key: string]: number }) => {
  return (
    <div>
      {Object.entries(stats).map(([type, count]) => {
        const typeName = semanticTypeMapping[type as keyof typeof semanticTypeMapping];
        if (!typeName) return null; 
        return (
          <div style={{ textAlign: 'left' }} key={type}>
            {typeName}: {count}
          </div>
        );
      })}
    </div>
  );
}

  return (
    <div className={styles.panelWrapper}>
      {/*<div>*/}
      {/*  空间树两个根节点，一个是空间层，一个是对应原始图纸最小识别单元*/}
      {/*</div>*/}
      <div>
        <Checkbox
          value={showUnknown}
          onChange={e => {
            setShowUnknown(!showUnknown);
          }}>未知</Checkbox>
        <Popover content={generateStatsContent(stats)} title="文件类型统计">
          <InfoCircleOutlined />
        </Popover>
      </div>
      <div className={styles.treeWrapper}>
        {/*<div>搜索文件、清空按钮</div>*/}
        <Tree.DirectoryTree
          showLine
          onExpand={onExpand}
          expandedKeys={expandKeys}
          treeData={spaceTree}
          titleRender={(data) => {
            // TODO: 当时为什么要加无用判断
            // if (true || typeof data.title === 'string')
            return (
              <Dropdown menu={{
                items: [
                  ...[
                    {
                      key: 'preview',
                      label: (
                        <div
                          onClick={e => {
                            e.preventDefault();
                            onPreview(data.spaceId);
                          }}
                        >
                          预览空间
                        </div>
                      ),
                    },
                  ],
                  ...getTuzhiTheme() !== 'szzj' ?
                    [
                      {
                        key: 'check',
                        label: (
                          <div
                            onClick={e => {
                              e.preventDefault();
                              onCheck(data.spaceId);
                            }}
                          >
                            审查空间
                          </div>
                        ),
                      },
                      {
                        key: 'custom-export',
                        label: '定制数据导出',
                        children: [
                          {
                            key: 'bcjson',
                            label: (
                              <div
                                onClick={e => {
                                  e.preventDefault();
                                  TuzhiVisService.getBCJSONBySpaceId(data.spaceId)
                                    .then(res => {
                                      saveAs(res.data, `model-${data.spaceId}.json`);
                                      message.success('BCJSON文件下载成功');
                                    });
                                }}
                              >
                                导出BCJSON
                              </div>
                            ),
                          },
                          {
                            key: 'tianhua',
                            label: (
                              <div
                                onClick={e => {
                                  const key = `tianhua-${data.spaceId}`;
                                  message.loading({content: '数据导出中，请稍后……', key});
                                  TuzhiVisService.exportDataBySpaceId(data.spaceId, 'tianhua-space')
                                    .then(res => {
                                      saveAs(res.data, `${key}.json`);
                                      message.success({content: '导出完成，保存成功', key, duration: 2});
                                    })
                                    .catch(err => {
                                      message.error({content: '导出失败，请检查网络', key, duration: 2});
                                    });
                                }}
                              >
                                天华空间信息导出
                              </div>
                            ),
                          },
                        ],
                      },
                    ] : [],
                ],
                style: {minWidth: 100, textAlign: 'center'},
              }} trigger={['contextMenu']}>
                <div className={styles.treeItem}>
                  <div className={styles.name}>{data.spaceId && data?.children?.length === 0 ?
                    <Tag>{data.spaceId.toString()}</Tag> : ''}{data.title}</div>
                </div>
              </Dropdown>
            );
          }}
        />
      </div>
    </div>
  );
}

export default SpaceTree;